import React, { useEffect, useState } from 'react';

const API_BASE = process.env.REACT_APP_API_BASE || '/api';

// Inline SVG placeholder (data URI)
const PLACEHOLDER =
  'data:image/svg+xml;utf8,' +
  encodeURIComponent(`
  <svg xmlns="http://www.w3.org/2000/svg" width="800" height="450" viewBox="0 0 800 450">
    <defs>
      <linearGradient id="g" x1="0" x2="1" y1="0" y2="1">
        <stop offset="0%" stop-color="#e5e7eb"/>
        <stop offset="100%" stop-color="#cbd5e1"/>
      </linearGradient>
    </defs>
    <rect width="800" height="450" fill="url(#g)"/>
    <g fill="#475569" font-family="Arial, Helvetica, sans-serif" text-anchor="middle">
      <text x="400" y="220" font-size="28" font-weight="700">Service</text>
      <text x="400" y="255" font-size="14" opacity="0.85">Image not available</text>
    </g>
  </svg>`);

// All styles live here (same file)
const styles = `
.svc-section {
  --brand: #1e3a8a;
  --bg: #f3f4f6;
  --text: #0f172a;
  --muted: #475569;
  background: var(--bg);
  padding: clamp(2rem, 5vw, 4rem) 1rem;
}
.svc-wrap { max-width: 1200px; margin: 0 auto; }
.svc-title {
  text-align: center;
  font-size: clamp(1.5rem, 3vw, 2rem);
  font-weight: 800;
  margin: 0 0 2rem;
  color: var(--text);
}
.svc-error {
  text-align: center;
  color: #b91c1c;
  margin-bottom: 1rem;
  font-weight: 600;
}
.svc-grid {
  display: grid;
  gap: 24px;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
}

/* Card: fixed equal height */
.svc-card {
  display: block;
  height: 320px;                 /* equal height */
  border-radius: 14px;
  overflow: visible;
  background: #fff;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.08);
  transform: translateZ(0);
  transition: transform .25s ease, box-shadow .25s ease;
  text-decoration: none;
}
.svc-card:hover {
  transform: scale(1.03);
  box-shadow: 0 14px 38px rgba(15, 23, 42, 0.14);
}
.svc-media {
  position: relative;
  height: 100%;
  border-radius: 14px;
  overflow: hidden;
}
.svc-media img {
  width: 100%;
  height: 100%;
  object-fit: cover;             /* keeps crop consistent */
  display: block;
  background: #e5e7eb;
}
.svc-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0,0,0,.2);
  opacity: 0;
  transition: opacity .25s ease;
}
.svc-card:hover .svc-overlay { opacity: 1; }

/* Label ribbon with notch */
.svc-label {
  position: absolute;
  bottom: 0;
  left: 0;
  background: #fff;
  padding: 10px 14px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  box-shadow: 0 6px 16px rgba(2,6,23,.12);
  clip-path: polygon(0 0, calc(100% - 16px) 0, 100% 16px, 100% 100%, 0% 100%);
}
.svc-accent { width: 24px; height: 3px; background: var(--brand); display: inline-block; }
.svc-label-text { font-weight: 700; color: var(--text); font-size: 0.95rem; }

/* Skeleton */
@keyframes shimmer { 0% { background-position: -450px 0; } 100% { background-position: 450px 0; } }
.svc-skel {
  height: 320px;
  border-radius: 14px;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.08);
}
.svc-skel-rect {
  height: 100%;
  background: linear-gradient(90deg, #e5e7eb 25%, #f3f4f6 37%, #e5e7eb 63%);
  background-size: 900px 100%;
  animation: shimmer 1.3s infinite;
}
@media (max-width: 480px) {
  .svc-card, .svc-skel { height: 280px; }
  .svc-label-text { font-size: .9rem; }
}
`;

export default function ServicesSection() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/services.php?active=1`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (!json.ok) throw new Error(json.error || 'Failed to load');
        if (!ignore) {
          setServices(Array.isArray(json.data) ? json.data : []);
          setErr('');
        }
      } catch (e) {
        if (!ignore) setErr(e.message || 'Network error');
      } finally {
        if (!ignore) setLoading(false);
      }
    })();
    return () => { ignore = true; };
  }, []);

  return (
    <section className="svc-section">
      {/* styles injected here */}
      <style>{styles}</style>

      <div className="svc-wrap">
        <h2 className="svc-title">How can we help?</h2>

        {err && <div className="svc-error">{err}</div>}

        <div className="svc-grid">
          {loading
            ? Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="svc-skel" aria-hidden="true">
                  <div className="svc-skel-rect" />
                </div>
              ))
            : services.map((svc) => {
                const to = (svc.href && String(svc.href)) || `/services/${svc.slug}`;
                const src = svc.full_image_url || svc.image_url || PLACEHOLDER;
                const isExternal = /^https?:\/\//i.test(to);

                return (
                  <a
                    key={svc.id}
                    className="svc-card"
                    href={to}
                    {...(!isExternal ? { 'data-router-link': to } : {})}
                    aria-label={`Learn more about ${svc.title}`}
                  >
                    <div className="svc-media">
                      <img
                        src={src}
                        alt={svc.title}
                        loading="lazy"
                        onError={(e) => {
                          if (e.currentTarget.src !== PLACEHOLDER) {
                            e.currentTarget.src = PLACEHOLDER;
                          }
                        }}
                      />
                      <div className="svc-overlay" />
                      <div className="svc-label">
                        <span className="svc-accent" />
                        <span className="svc-label-text">{svc.title}</span>
                      </div>
                    </div>
                  </a>
                );
              })}
        </div>
      </div>
    </section>
  );
}
